import React, { useContext, useState, useCallback } from 'react';
import { Autocomplete, useLoadScript, useJsApiLoader } from '@react-google-maps/api';
import './AddressSearch.css';
import { WindowSizeContext, AddressSearchContext } from '../../App';
import FormatGoogleAutocompleteResult from '/src/services/FormatGoogleAutocompleteResult';
import axios from 'axios';
import NormalizeDynamoDBItem from '/src/services/NormalizeDynamoDBItem';
import { decompressData } from '/src/utils/decompressData';
import { useNavigate } from 'react-router-dom';
import LogUserAddressSearch from '/src/services/Logging/LogUserAddressSearch';
import { House } from 'lucide-react';
import CreateCloudfrontUrl from '/src/services/CreateCloudfrontUrl';

// import { fetchQGDData } from '/src/services/FetchGeoData';
// import { fetchAllBoundaryData } from '/src/services/FetchAllBoundaryData';


const libraries = ['places'];
const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;

const AddressSearch = ({
  locationType
}) => {
  const { deviceType } = useContext(WindowSizeContext);
  const {
    addressSearchModelingTracker,
    setAddressSearchModelingTracker,
    addressSearchInProgress,
    setAddressSearchInProgress,
    addressSearchSecondaryConfirmation,
    setAddressSearchSecondaryConfirmation,
    addressSearchModelingInProgress,
    setAddressSearchModelingInProgress,
    addressSearchGoogleAutocompleteResult,
    setAddressSearchGoogleAutocompleteResult,
    setAddressSearchModelingCompleted
  } = useContext(AddressSearchContext);
  
  const { LOG_GA_EVENT, user } = useContext(AddressSearchContext);

  
  const [inputAddress, setInputAddress] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const locationClass = locationType === 'header' ? 'header' : 'regular';

  const regularPlaceholderText = "Search an address..."
  // const regularPlaceholderText = "Enter an address to catch up..."
  const otherPlaceholderText = "Search an address..."

  const placeholderText = locationType === 'regular' ? regularPlaceholderText : otherPlaceholderText

  // Create const that is 'search-input' if deviceType is desktop, otherwise 'search-input-mobile'
  const searchInputClass = deviceType === 'desktop' ? `address-search-container-input ${locationClass}` : `address-search-container-input-mobile ${locationClass}`;

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
    libraries,
    version: "weekly",
    loading: "async",
  });

  // Add autocomplete options configuration
  const autocompleteOptions = {
    componentRestrictions: { country: 'us' },
    types: ['address'],  // This restricts to street addresses, filtering out businesses/POIs
    // fields: ['formatted_address', 'address_components']
  };

  let autocomplete = null;

  const onLoad = useCallback((autocompleteInstance) => {
    autocomplete = autocompleteInstance;
  }, []);

  const onPlaceChanged = useCallback(() => {
    if (autocomplete !== null) {
      const place = autocomplete.getPlace();
      
      // Only proceed if we have a valid place with an ID
      if (place && place.place_id) {

        let eventId = 'address_search_nav';
        if (locationType === 'regular') {
          eventId = 'address_search_hero';
        }
        const additionalParams = {
          google_place_id: place.place_id,
          google_place_formatted_address: place.formatted_address,
        };

        LOG_GA_EVENT(eventId, additionalParams);

        // console.log("A ----> place", place);
        handleAddressSelect(place);
        setInputAddress('');
      }
    }
  }, []);

  const handleAddressSelect = useCallback(async (place) => {
    
    const formattedAddress = FormatGoogleAutocompleteResult(place);
    // console.log('formattedAddress', formattedAddress);
    const place_id = formattedAddress.placeId;
    // console.log("B ----> GOOGLE PLACE OBJECT", place, formattedAddress, place_id);


    // Check local storage for existing address
    const user = JSON.parse(localStorage.getItem('user'));
    // console.log("C ----> AddressSearch.jsx user", user);
    if (user?.addresses) {
      // console.log("D ----> AddressSearch.jsx user.addresses", user.addresses);
      const existingAddress = user.addresses.find(addr => addr.place_id === place_id);
      // console.log("E ----> AddressSearch.jsx existingAddress", existingAddress);
      if (existingAddress) {
        const userIdToUse = user.user_id || null;
        const sessionIdToUse = user.sessionId || null;
        const puidToUse = user.puid;

        // We need to log the user search for the property
        const logObject = {
          property_id: existingAddress.address_id,
          first_time_search: false,
          user_id: puidToUse,
          session_id: sessionIdToUse
        }
        LogUserAddressSearch(logObject);
        
        // The send them to the property page
        navigate(`/properties/${existingAddress.address_id}/big-picture`);
        return;
      }
    }
    // OLD WAY
    setAddressSearchInProgress(true);
    setAddressSearchGoogleAutocompleteResult(formattedAddress);
    // NEW WAY
    // console.log("G ----> Setting the address search modeling tracker");
    setAddressSearchModelingTracker(prevState => ({
      ...prevState,
      searchInProgress: true,
      searchType: 'input',
      addressDetailsGoogle: formattedAddress,
      googlePlaceId: place_id
    }));

  }, [navigate]);

  // Add loading and error handling
  if (loadError) {
    return <div>Error loading maps</div>;
  }

  if (!isLoaded) {
    return <div>Loading...</div>;
  }

  const houseSearchIconFilename = "quoll-house-search-icon.svg"
  const houseSearchIconUrl = CreateCloudfrontUrl(houseSearchIconFilename);

  return (
    <div className={`address-search-container ${locationClass} ${deviceType}`}>
      {deviceType !== 'mobile' && (
        <div className="address-search-container-icon-container">
          <img src={houseSearchIconUrl} alt="House Search Icon" className="address-search-container-icon-image" />
        </div>
      )}
      {isLoaded && (
        <Autocomplete
          onLoad={onLoad}
          onPlaceChanged={onPlaceChanged}
          style={{ width: '100%' }}
          options={autocompleteOptions}
          className={`address-search-container-input ${locationClass} ${deviceType}`}
        >
          <input
            type="text"
            placeholder={placeholderText}
            value={inputAddress}
            onChange={(e) => setInputAddress(e.target.value)}
            disabled={isLoading}
          />
        </Autocomplete>
      )}
    </div>
  );
};

export default AddressSearch;
